// /en/company-setup — EN hub for Thailand company formation
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, Building2, Briefcase, Globe, Scale } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/company-setup/")({
    loader: async () => {
    const { COMPANY_ENTITIES } = await import("@/data/company-setup-types");
    return { __d: { COMPANY_ENTITIES } };
  },
head: ({ loaderData }) => {
    const { COMPANY_ENTITIES } = loaderData!.__d as typeof import("@/data/company-setup-types");
    const url = `${SITE}/en/company-setup`;
    const title = `Thailand Company Registration 2026 — BOI / FBL / Branch / Rep Office / IBC | NYC`;
    const desc = `Complete Thailand entity setup — Thai Limited, BOI Promotion (CIT exempt 3-13 yrs), Foreign Business License, Branch Office, Representative Office, IBC/ROH, US Treaty of Amity, Partnership. With visa + work permit. Talk to a specialist today.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/company-setup", "en") }, { name: "description", content: ctaDesc(desc, "/en/company-setup", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/company-setup", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/company-setup", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "Thailand company registration, BOI promotion, Foreign Business License, branch office Thailand, representative office, IBC Thailand, US Treaty of Amity, Thai Limited Company, DBD registration" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/company-setup", "/en/company-setup")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "en-US",
              mainEntity: {
                "@type": "ItemList", numberOfItems: COMPANY_ENTITIES.length,
                itemListElement: COMPANY_ENTITIES.map((e, i) => ({
                  "@type": "ListItem", position: i + 1, name: e.titleEn, url: `${SITE}/en/company-setup/${e.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Thailand Company Formation Service",
              serviceType: "Company Registration & Corporate Structuring (DBD / BOI / FBL)",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "14000", priceCurrency: "THB", description: "From 14,000 THB — Partnership / Thai Limited end-to-end registration" },
            }),
          },
          faqPageSchema([
            { q: "What's needed to register a Thai company?", a: "Under Civil & Commercial Code §1096, a Thai Limited Company needs ≥2 shareholders, par value ≥ THB 5/share (we recommend THB 2M registered capital per foreign work permit to satisfy MoL ratio), a Thailand office, an MOA filed with DBD, and a statutory meeting. NYC completes all 4 DBD steps — name reservation, MOA, registration, TIN — in 7 business days." },
            { q: "BOI vs FBL vs Treaty — which to use?", a: "BOI = tax holiday + 100% foreign in promoted activities · FBL = license for restricted businesses · Treaty of Amity = 100% ownership for US citizens only. Choose by business profile." },
            { q: "Branch vs Subsidiary — what's the difference?", a: "Branch = not a separate entity, parent has unlimited liability + needs FBL · Subsidiary (Thai Limited) = separate entity, limited liability, no FBL if Thais hold 51%+." },
            { q: "How many Thais per work permit?", a: "Under the Foreign Employment Act B.E.2521 and Ministry of Labour Notification No.7, a non-BOI Thai company must employ 4 Thai nationals on social security per 1 foreign work permit, plus registered capital of THB 2 million per foreigner. BOI-promoted and Treaty of Amity entities are exempt — NYC selects the right vehicle during the free strategy call." },
            { q: "What are NYC's fees?", a: "All-inclusive concierge fees (excluding DBD government fees and registered capital): Ordinary Partnership from THB 14,000 · Thai Limited Company THB 19,000 · Treaty of Amity US-Thai Limited THB 65,000 · Foreign Business Licence (FBL) THB 85,000 · BOI Promotion THB 95,000 · International Business Centre (IBC) THB 150,000. Every quote issued on a Revenue Department e-Tax Invoice with itemised scope." },
          ]),
        ]),
      ],
    };
  },
  component: CompanyHubEn,
});

function CompanyHubEn() {
  const { COMPANY_ENTITIES } = (Route.useLoaderData() as { __d: typeof import("@/data/company-setup-types") }).__d;
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link><span className="mx-2">/</span><span>Company Setup</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Building2 className="mr-1 inline h-3 w-3" /> DBD · BOI · FBL · Treaty · 8 entity types
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">Thailand Company Setup — All Entity Types</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Thai Limited · BOI Promotion · Foreign Business License · Branch Office · Representative Office · International Business Center (IBC) · US Treaty of Amity · Partnership — pick the structure that fits, with visa + work permit included.
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">Which entity should I choose?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              <strong>Thai Limited (49/51)</strong> = SMEs/restaurants · <strong>BOI</strong> = CIT exempt 3-13 yrs + 100% foreign (manufacturing, software, EV) · <strong>FBL</strong> = restricted businesses · <strong>Branch</strong> = parent extension, 3M capital · <strong>Rep Office</strong> = market research, no revenue · <strong>IBC</strong> = regional HQ, 3-8% CIT · <strong>Treaty of Amity</strong> = US persons 100% · <strong>Partnership</strong> = small family business
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66-92-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/en/work-permit" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <Briefcase className="h-4 w-4" /> Work Permit
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">8 entity structures</h2>
          <p className="mt-2 text-sm text-muted-foreground">Choose by business profile · click for requirements, documents, steps, and full fees.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {COMPANY_ENTITIES.map((e) => (
              <Link
                key={e.slug}
                to="/en/company-setup/$entity"
                params={{ entity: e.slug }}
                className="group rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                data-track-zone="company-hub-en"
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{e.icon}</span>
                  <div className="font-bold text-sm">{e.titleEn}</div>
                </div>
                <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{e.taglineEn}</div>
                <div className="mt-3 text-[11px] font-semibold text-foreground/80">💰 {e.capitalEn}</div>
                <div className="mt-1 text-[11px] text-muted-foreground">🌐 {e.foreignOwnershipEn}</div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">THB {e.servicePriceTHB.toLocaleString()}</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-primary">
                    View <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold"><Scale className="mr-2 inline h-5 w-5 text-primary" />Related services</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Work Permit", d: "Non-B visa + work permit", to: "/en/work-permit" as const },
              { t: "LTR Visa", d: "10-year visa · HSP 17% flat", to: "/en/ltr" as const },
              { t: "DTV Visa", d: "5-year · Workcation/Freelancer", to: "/en/dtv" as const },
              { t: "Visa Compliance", d: "TM30 / 90-day / Re-entry", to: "/en/visa-compliance" as const },
              { t: "MFA + Apostille", d: "Legalize parent company docs", to: "/en/mfa-legalisation" as const },
              { t: "Document Translation", d: "MOA, FS, Resolution", to: "/en/translate" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    <RelatedHubGrid locale="en" /></main>
  );
}
